import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { PARTNER_BRAND } from '../../partnerDashboardTheme';
import {
  buildDeclineJoinRequestPayload,
  MEMBERSHIP_REJECTION_REASONS,
  OTHER_DECLINE_REASON_CODE,
} from '../joinRequestDialogData';
import { COMPANY_INFO_FIELD_SX } from '../../../PartnerSettings/PartnerSettingsSection/companyInfoStyles';

const outlinedButtonSx = {
  minWidth: 120,
  height: 44,
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: 1.5,
  color: PARTNER_BRAND,
  borderColor: PARTNER_BRAND,
  '&:hover': {
    borderColor: 'primary.dark',
    bgcolor: 'rgba(224, 104, 10, 0.04)',
  },
};

const containedButtonSx = {
  minWidth: 120,
  height: 44,
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: 1.5,
  bgcolor: PARTNER_BRAND,
  boxShadow: 'none',
  '&:hover': {
    bgcolor: 'primary.dark',
    boxShadow: 'none',
  },
};

const radioSx = {
  color: 'text.disabled',
  alignSelf: 'flex-start',
  mt: 0.25,
  '&.Mui-checked': {
    color: PARTNER_BRAND,
  },
};

const RejectMembershipRequestDialog = ({
  open,
  applicantName,
  rejecting = false,
  onClose,
  onContinue,
}) => {
  const [reasonId, setReasonId] = useState('');
  const [otherReason, setOtherReason] = useState('');

  useEffect(() => {
    if (!open) return;
    setReasonId('');
    setOtherReason('');
  }, [open, applicantName]);

  const canContinue =
    Boolean(reasonId) && (reasonId !== OTHER_DECLINE_REASON_CODE || Boolean(otherReason.trim()));

  const handleContinue = () => {
    if (!canContinue) return;

    const payload = buildDeclineJoinRequestPayload({
      declineReasonCode: reasonId,
      declineReasonText: otherReason,
    });
    onContinue?.(payload);
  };

  return (
    <Dialog
      open={open}
      onClose={rejecting ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          pr: 1,
          pb: 1,
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '1.125rem', color: 'text.primary' }}>
            Reject Membership Request
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.6 }}>
            Please select a reason for rejecting this request. This reason will be used to
            personalize the message sent to the applicant.
          </Typography>
        </Box>
        <IconButton
          aria-label="Close reject membership dialog"
          onClick={onClose}
          disabled={rejecting}
          size="small"
          sx={{ mt: -0.5 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        <RadioGroup value={reasonId} onChange={(event) => setReasonId(event.target.value)}>
          {MEMBERSHIP_REJECTION_REASONS.map((option) => (
            <Box
              key={option.id}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '10px',
                px: 1.5,
                py: 1.25,
                mb: 1.25,
                bgcolor: reasonId === option.id ? '#fffaf6' : '#fff',
              }}
            >
              <FormControlLabel
                value={option.id}
                control={<Radio size="small" sx={radioSx} />}
                label={
                  <Box sx={{ py: 0.25 }}>
                    <Typography
                      sx={{ fontWeight: 700, fontSize: '0.875rem', color: 'text.primary' }}
                    >
                      {option.label}
                    </Typography>
                    {option.description ? (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.25, lineHeight: 1.5 }}
                      >
                        {option.description}
                      </Typography>
                    ) : null}
                    {option.id === OTHER_DECLINE_REASON_CODE ? (
                      <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        placeholder="Please provide a specific reason..."
                        value={otherReason}
                        onChange={(event) => setOtherReason(event.target.value)}
                        disabled={reasonId !== OTHER_DECLINE_REASON_CODE}
                        sx={{ ...COMPANY_INFO_FIELD_SX, mt: 1.25 }}
                      />
                    ) : null}
                  </Box>
                }
                sx={{
                  alignItems: 'flex-start',
                  mx: 0,
                  width: '100%',
                  '& .MuiFormControlLabel-label': { width: '100%' },
                }}
              />
            </Box>
          ))}
        </RadioGroup>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 3, pt: 0 }}>
        <Button variant="outlined" disabled={rejecting} onClick={onClose} sx={outlinedButtonSx}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          disabled={rejecting || !canContinue}
          onClick={handleContinue}
          sx={containedButtonSx}
        >
          {rejecting ? 'Continuing…' : 'Continue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RejectMembershipRequestDialog;
